export interface System {
  id: number;
  name: string;
  url: string;
  description: string;
  icon: string;
  accent: AccentName;
  sortOrder: number;
}

export interface Me {
  id: number;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
}

export interface User {
  id: number;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AuthConfig {
  googleConfigured: boolean;
  allowedDomain: string;
}

export type AccentName =
  | 'slate'
  | 'sky'
  | 'amber'
  | 'rose'
  | 'emerald'
  | 'violet'
  | 'indigo'
  | 'cyan'
  | 'pink'
  | 'lime'
  | 'orange';

export const ACCENTS: AccentName[] = [
  'slate',
  'sky',
  'amber',
  'rose',
  'emerald',
  'violet',
  'indigo',
  'cyan',
  'pink',
  'lime',
  'orange',
];

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : null) ?? `Request failed: ${res.status}`;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  authConfig: () => request<AuthConfig>('GET', '/api/auth/config'),
  me: () => request<Me>('GET', '/api/auth/me'),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),

  listSystems: () => request<System[]>('GET', '/api/systems'),
  createSystem: (data: Omit<System, 'id' | 'sortOrder'>) =>
    request<System>('POST', '/api/systems', data),
  updateSystem: (id: number, data: Partial<Omit<System, 'id' | 'sortOrder'>>) =>
    request<System>('PATCH', `/api/systems/${id}`, data),
  deleteSystem: (id: number) => request<{ ok: true }>('DELETE', `/api/systems/${id}`),
  reorderSystems: (ids: number[]) => request<{ ok: true }>('POST', '/api/systems/reorder', { ids }),
  reorderMySystems: (ids: number[]) => request<{ ok: true }>('POST', '/api/systems/my-order', { ids }),

  listUsers: () => request<User[]>('GET', '/api/users'),
  addAdmin: (email: string) => request<User>('POST', '/api/users/admins', { email }),
  removeAdmin: (id: number) => request<{ ok: true }>('DELETE', `/api/users/admins/${id}`),
};
